import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Lock, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { resetPassword } from "../lib/api";
import { Button } from "../components/ui/button";

const PASSWORD_MIN = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeTokenSignIn } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < PASSWORD_MIN) {
      setError(`Choose a password of at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await resetPassword(token, password);
      const u = completeTokenSignIn(data);
      toast.success("Password updated", {
        description: `You're signed in as ${u.email}.`,
      });
      navigate("/check", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === "string" && detail) ||
          "This reset link is invalid or has expired. Please request a new one.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full h-14 pl-11 pr-4 rounded-2xl bg-white border border-surface-line text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-peach-100 focus:border-peach-200 transition-all";

  return (
    <main
      data-testid="reset-password-page"
      className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 bg-cream"
    >
      <div className="max-w-md w-full">
        {!token ? (
          <div className="text-center">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-avoid-bg text-avoid-deep items-center justify-center border border-avoid-border">
              <AlertTriangle className="w-6 h-6" />
            </span>
            <h1 className="font-bold text-2xl text-ink mt-5 tracking-[-0.02em]">
              This reset link is incomplete.
            </h1>
            <button
              data-testid="reset-back-home"
              onClick={() => navigate("/")}
              className="mt-6 text-sm text-ink-soft underline underline-offset-4"
            >
              Back to home
            </button>
          </div>
        ) : (
          <div>
            <span className="inline-flex w-14 h-14 rounded-2xl bg-safe-bg text-safe-deep items-center justify-center border border-safe-border">
              <ShieldCheck className="w-6 h-6" />
            </span>
            <h1 className="font-bold text-2xl sm:text-3xl text-ink mt-5 tracking-[-0.02em]">
              Choose a new password
            </h1>
            <p className="text-ink-soft/85 mt-2 text-[15px]">
              Enter a new password for your Blendsafe account.
            </p>

            {error && (
              <div
                data-testid="reset-error"
                className="mt-5 rounded-xl bg-avoid-bg border border-avoid-border text-avoid-deep text-[13px] px-4 py-3"
              >
                {error}
              </div>
            )}

            <form onSubmit={submit} className="mt-5 space-y-4">
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  placeholder={`New password (min ${PASSWORD_MIN} chars)`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-light" />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputCls}
                />
              </div>
              <Button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={submitting}
                className="w-full h-14 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[15px] font-medium transition-all hover:scale-[1.01] active:scale-[0.99]"
              >
                {submitting ? "Updating…" : "Update password"}
                {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
